import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Package, Truck } from "lucide-react";
import { toast } from "sonner";

const statusInfo = {
  aguardando_solicitacao: { label: "Aguardando aprovação", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  aprovado: { label: "Aprovado", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  enviado: { label: "Enviado", cls: "bg-violet-100 text-violet-800 border-violet-200" },
  entregue: { label: "Entregue", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  cancelado: { label: "Cancelado", cls: "bg-rose-100 text-rose-800 border-rose-200" },
};

export default function AdminKitsVIP() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [tracking, setTracking] = useState("");

  const load = () => {
    const params = {};
    if (filter && filter !== "all") params.status = filter;
    api.get("/admin/kit-vip", { params }).then(r => setItems(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openItem = (k) => { setOpen(k); setNewStatus(k.status); setTracking(k.tracking_code || ""); };
  const send = async () => {
    if (!open) return;
    try {
      await api.put(`/admin/kit-vip/${open.id}`, { status: newStatus, tracking_code: tracking });
      toast.success("Atualizado");
      setOpen(null); load();
    } catch { toast.error("Erro"); }
  };

  return (
    <AdminLayout title="Kits VIP">
      <Card className="rounded-xl"><CardContent className="p-4 flex flex-wrap gap-3 items-end">
        <div><p className="text-xs text-muted-foreground mb-1">Status</p>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[220px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="aguardando_solicitacao">Aguardando aprovação</SelectItem>
              <SelectItem value="aprovado">Aprovado</SelectItem>
              <SelectItem value="enviado">Enviado</SelectItem>
              <SelectItem value="entregue">Entregue</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardContent></Card>
      <Card className="rounded-xl mt-4"><CardContent className="p-0 overflow-x-auto">
        <Table><TableHeader><TableRow>
          <TableHead>Aluno</TableHead><TableHead>Endereço</TableHead><TableHead>Status</TableHead><TableHead>Código</TableHead><TableHead className="text-right">Ação</TableHead>
        </TableRow></TableHeader>
          <TableBody>
            {items.map(k => (
              <TableRow key={k.id}>
                <TableCell><p className="font-medium">{k.user_name}</p><p className="text-xs text-muted-foreground font-mono-id">{k.user_ceap_id}</p></TableCell>
                <TableCell className="text-xs max-w-[260px]"><p className="line-clamp-2">{k.address}</p><p className="text-muted-foreground">{k.cep}</p></TableCell>
                <TableCell><Badge className={statusInfo[k.status]?.cls} variant="outline">{statusInfo[k.status]?.label || k.status}</Badge></TableCell>
                <TableCell className="font-mono-id text-xs">{k.tracking_code || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => openItem(k)} data-testid={`kit-update-${k.id}`}><Truck className="w-4 h-4 mr-1" /> Atualizar</Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">Nenhum kit solicitado</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent></Card>
      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent className="max-w-[460px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Atualizar Kit VIP</DialogTitle>
            <DialogDescription>Aprove, atualize status e código de rastreio.</DialogDescription>
          </DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--secondary))] p-3 text-sm">
                <p><strong>Aluno:</strong> {open.user_name}</p>
                <p><strong>Endereço:</strong> {open.address}</p>
                {open.cep && <p><strong>CEP:</strong> {open.cep}</p>}
                {open.notes && <p className="mt-1"><strong>Obs:</strong> {open.notes}</p>}
              </div>
              <div><Label>Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aguardando_solicitacao">Aguardando aprovação</SelectItem>
                    <SelectItem value="aprovado">Aprovado</SelectItem>
                    <SelectItem value="enviado">Enviado</SelectItem>
                    <SelectItem value="entregue">Entregue</SelectItem>
                    <SelectItem value="cancelado">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Código de rastreio</Label><Input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Ex: BR123456789" /></div>
              <Button onClick={send} className="w-full" data-testid="kit-save">Salvar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
